import React, { FC, PropsWithChildren } from 'react';
import { Button, buttonClasses, ButtonProps } from '@mui/material';

import { Typography } from '../Typography';
import { Stack } from '../Stack';


interface AtomTabButtonProps extends Omit<ButtonProps, 'size' | 'type'> {
    type?: 'white' | 'black' | 'transparent' | 'grey';
    count?: number;
    icon?: React.ReactNode;
    size?: 'L' | 'M' | 'S';
    active?: boolean;
}

const propsBySizeMap: Record<AtomTabButtonProps['size'], { height: number, px: number }> = {
    L: {
        height: 12,
        px: 4,
    },
    M: {
        height: 10,
        px: 4,
    },
    S: {
        height: 8,
        px: 3,
    },
};

const styleByTypeMap: Record<AtomTabButtonProps['type'], ButtonProps['sx']> = {
    black: {
        bgcolor: 'grayscale.shade0',
        color: 'grayscale.shade95',
        '&:hover:enabled': {
            bgcolor: 'grayscale.shade20',
        },
        '&:active:enabled, &:focus:enabled': {
            bgcolor: 'grayscale.shade95',
            color: 'grayscale.shade0',
        },
        '&:disabled': {
            bgcolor: 'grayscale.shade10',
            color: 'grayscale.shade80',
        },
    },
    grey: {
        bgcolor: 'grayscale.shade10',
        color: 'grayscale.shade95',
        '&:hover:enabled': {
            bgcolor: 'grayscale.shade20',
        },
        '&:active:enabled, &:focus:enabled': {
            bgcolor: 'grayscale.shade95',
            color: 'grayscale.shade0',
        },
        '&:disabled': {
            bgcolor: 'grayscale.shade10',
            color: 'grayscale.shade80',
        },
    },
    transparent: {
        bgcolor: 'transparent',
        color: 'grayscale.shade95',
        '&:hover:enabled': {
            bgcolor: 'grayscale.shade5',
        },
        '&:focus:enabled': {
            bgcolor: 'grayscale.shade10',
        },
        '&:active:enabled': {
            bgcolor: 'grayscale.shade20',
        },
        '&:disabled': {
            color: 'grayscale.shade80',
        },
    },
    white: {
        bgcolor: 'grayscale.shade0',
        color: 'grayscale.shade95',
        '&:enabled:hover': {
            bgcolor: 'grayscale.shade20',
        },
        '&:focus:enabled': {
            bgcolor: 'general.primary50',
            color: 'grayscale.shade0',
        },
        '&:active:enabled': {
            bgcolor: 'general.primary60',
            color: 'grayscale.shade0',
        },
        '&:disabled': {
            bgcolor: 'general.primary10',
            color: 'grayscale.shade80',
        },
    },
};

const activeStyleByTypeMap: Record<AtomTabButtonProps['type'], ButtonProps['sx']> = {
    black: {
        '&, &: disabled, &:hover, &:focus': {
            bgcolor: 'grayscale.shade95',
            color: 'grayscale.shade0',
        },
    },
    grey: {
        '&, &:disabled, &:hover, &:focus': {
            bgcolor: 'grayscale.shade95',
            color: 'grayscale.shade0',
        },
    },
    transparent: {
        '&, &:disabled, &:hover, &:focus': {
            bgcolor: 'grayscale.shade10',
            color: 'grayscale.shade95',
        },
    },
    white: {
        '&, &:disabled, &:hover, &:focus': {
            bgcolor: 'general.primary60',
            color: 'grayscale.shade0',
        },
    },
};

export const AtomTabButton: FC<PropsWithChildren<AtomTabButtonProps>> = ({
    type = 'white', children, count, icon, size = 'M', active, disabled, sx, ...props
}) => (
    <Button
        startIcon={icon}
        disableRipple
        disableFocusRipple
        disableTouchRipple
        onMouseUp={e => e.currentTarget.blur()}
        disabled={active || disabled}
        {...props}
        {...count && {
            endIcon: (
                <Stack
                    justifyContent='center'
                    alignItems='center'
                    borderRadius={3}
                    height='1em'
                    width='1em'
                >
                    <Typography variant='boldCaption2'>
                        {count}
                    </Typography>
                </Stack>
            ),
        }}
        sx={[({ spacing }) => ({
            borderRadius: spacing(25),
            [`& .${buttonClasses.endIcon} > *`]: {
                bgcolor: 'infoCustom.red50',
                color: 'grayscale.shade0',
            },
            ...type !== 'transparent' && {
                [`&:active .${buttonClasses.endIcon} > *, &:focus .${buttonClasses.endIcon} > *`]: {
                    bgcolor: 'grayscale.shade0',
                    color: 'infoCustom.red50',
                },
            },
            [`&:disabled .${buttonClasses.endIcon} > *`]: {
                bgcolor: 'grayscale.shade40',
                color: 'grayscale.shade85',
            },
            [`.${buttonClasses.startIcon}`]: {
                ml: 0,
            },
            [`.${buttonClasses.endIcon}`]: {
                mr: 0,
            },
            height: spacing(propsBySizeMap[size].height),
            px: spacing(propsBySizeMap[size].px),
            ...active ? activeStyleByTypeMap[type] : styleByTypeMap[type],
        }),
        ...Array.isArray(sx) ? sx : [sx]]}
    >
        <Typography variant='mediumCaption1'>
            {children}
        </Typography>
    </Button>
);
